import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Camera, Image, Heart, Users, HardDrive, Smile, LogOut } from "lucide-react";
import { EVENTS, EVENT_COLORS } from "./config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PASSWORD = "paritale2026";

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-icon" style={{ background: color + "22", color }}>
        <Icon size={22} />
      </div>
      <div className="admin-stat-body">
        <div className="admin-stat-value">{value}</div>
        <div className="admin-stat-label">{label}</div>
        {sub && <div className="admin-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

function Bar({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="admin-bar-row">
      <div className="admin-bar-label">{label}</div>
      <div className="admin-bar-track">
        <div className="admin-bar-fill" style={{ width: `${pct}%`, background: color || "#B8952A" }} />
      </div>
      <div className="admin-bar-count">{value}</div>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed]     = useState(() => sessionStorage.getItem("adminAuthed") === "1");
  const [password, setPassword] = useState("");
  const [pwError, setPwError]   = useState(false);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (authed) loadStats();
  }, [authed]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem("adminAuthed", "1");
      setAuthed(true);
    } else {
      setPwError(true);
      setTimeout(() => setPwError(false), 1500);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuthed");
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <h2 className="admin-login-title">Admin</h2>
          <p className="admin-login-sub">#PaRiTaleWedding</p>
          <input
            type="password"
            className={`admin-pw-input${pwError ? " error" : ""}`}
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLogin()}
            autoFocus
          />
          {pwError && <p className="admin-pw-error">Incorrect password</p>}
          <button className="admin-login-btn" onClick={handleLogin}>Enter</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Dashboard</h1>
          <p className="admin-subtitle">#PaRiTaleWedding — Admin</p>
        </div>
        <div className="admin-header-actions">
          <button className="admin-refresh-btn" onClick={loadStats} disabled={loading}>
            {loading ? <Loader2 size={15} className="spinning" /> : "Refresh"}
          </button>
          <button className="admin-logout-btn" onClick={handleLogout}>
            <LogOut size={15} /> Logout
          </button>
        </div>
      </div>

      {loading && !stats ? (
        <div className="admin-loading"><Loader2 size={36} className="spinning" /></div>
      ) : stats ? (
        <>
          {/* Stat cards */}
          <div className="admin-stat-grid">
            <StatCard icon={Image}     label="Photos"           value={stats.totals.photos}        color="#B8952A" />
            <StatCard icon={Camera}    label="Videos"           value={stats.totals.videos}         color="#7C6FCD" />
            <StatCard icon={Users}     label="Registered Guests" value={stats.totals.registeredUsers} color="#4ECDC4" />
            <StatCard icon={Heart}     label="Total Likes"      value={stats.totals.totalLikes}     color="#FF6B6B" />
            <StatCard icon={Smile}     label="Face-Tagged Photos" value={stats.totals.faceTagged}  color="#6FCF97"
              sub={`of ${stats.totals.photos} photos`} />
            <StatCard icon={HardDrive} label="Storage Used"     value={`${stats.totals.storageGB} GB`} color="#F2994A" />
          </div>

          <div className="admin-two-col">
            {/* Per Event */}
            <div className="admin-panel">
              <h3 className="admin-panel-title">Photos by Event</h3>
              {Object.entries(stats.perEvent).map(([ev, count]) => {
                const max = Math.max(...Object.values(stats.perEvent));
                const color = EVENT_COLORS[ev]?.bg || "#B8952A";
                return <Bar key={ev} label={ev} value={count} max={max} color={color} />;
              })}
            </div>

            {/* Per Uploader */}
            <div className="admin-panel">
              <h3 className="admin-panel-title">Photos by Guest</h3>
              {Object.entries(stats.perUploader).map(([name, count]) => {
                const max = Math.max(...Object.values(stats.perUploader));
                return <Bar key={name} label={name} value={count} max={max} />;
              })}
            </div>
          </div>

          {/* Top Photos */}
          {stats.topPhotos.length > 0 && (
            <div className="admin-panel">
              <h3 className="admin-panel-title">Most Liked Photos</h3>
              <div className="admin-top-photos">
                {stats.topPhotos.map((p, i) => (
                  <div key={p.id} className="admin-top-photo">
                    <div className="admin-top-rank">#{i + 1}</div>
                    <img src={p.thumbnailUrl} alt="" className="admin-top-img" />
                    <div className="admin-top-meta">
                      <span className="admin-top-likes"><Heart size={12} fill="#FF6B6B" stroke="none" /> {p.likeCount}</span>
                      <span className="admin-top-uploader">{p.uploader}</span>
                      {p.event && p.event !== "Untagged" && (
                        <span className="admin-top-event" style={{ background: EVENT_COLORS[p.event]?.bg }}>
                          {p.event}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Registered Users */}
          <div className="admin-panel">
            <h3 className="admin-panel-title">
              Registered Guests
              <span className="admin-panel-badge">{stats.registeredUsers.length}</span>
              <span className="admin-panel-badge" style={{ background: "#6FCF9722", color: "#6FCF97" }}>
                {stats.encodedFaces.length} face-encoded
              </span>
            </h3>
            <div className="admin-users-grid">
              {stats.registeredUsers.map(u => (
                <div key={u.name} className="admin-user-chip">
                  {u.selfieUrl && <img src={u.selfieUrl} alt={u.name} className="admin-user-avatar" />}
                  <span>{u.name}</span>
                  {stats.encodedFaces.includes(
                    u.name.replace(/[^a-zA-Z0-9\s]/g, "").replace(/\s+/g, "_").toLowerCase()
                  ) && <span className="admin-face-dot" title="Face encoded" />}
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
